import { isDeepEqual } from '@attain-apps/data-utils'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjsUtils from '@attain-apps/rxjs-utils'
import * as rxjs from 'rxjs'

import type * as variableOverrides from '../../../../../../variable-overrides'
import type * as aliases from '../../../../../aliases'
import type * as currentRendering from '../../../feature'
import type * as widgets from '../../widgets'
import type { Feature } from '../feature'
/**
 * Time in milliseconds to debounce the rendering process either on
 * widgetVariables or other relevant changes
 *
 * @see {@link variableOverrides.Feature}
 */
const DEBOUNCE_TIME = 500

export const triggerModalWidgetRendering = store.effects.create(
  (
    feature: Feature,
    currentRenderingFeature: currentRendering.Feature,
    widgetsFeature: widgets.Feature,
    variablesFeature: variableOverrides.Feature,
    aliasesFeature: aliases.Feature,
  ) =>
    feature.selectors.widgetRef.asObservable.pipe(
      rxjsUtils.latestFrom(widgetRef => ({
        // Get page context to enable modal widget rendering within the page
        // environment
        pageContext: currentRenderingFeature.selectors.context.asObservable,
        widgetRef: rxjs.of(widgetRef),
      })),
      // Skip rendering when pageContext or widgetRef are missing
      rxjs.filter(
        // Type guard to verify both pageContext and widgetRef exist
        // This ensures type safety in the subsequent switchMap operation
        (
          data,
        ): data is typeof data & {
          pageContext: NonNullable<typeof data.pageContext>
          widgetRef: NonNullable<typeof data.widgetRef>
        } => !!data.widgetRef && !!data.pageContext,
      ),
      rxjs.switchMap(({ pageContext, widgetRef }) =>
        aliasesFeature.selectors
          // NOTE: Determine widget ID by resolving either direct ID reference or
          // alias lookup
          .getWidgetId(widgetRef.widget)
          .asObservable.pipe(
            rxjs.switchMap(
              widgetId =>
                variablesFeature.selectors.getVariableOverrides(
                  widgetId
                    ? {
                        widget: {
                          appId: pageContext.appId,
                          pageId: pageContext.pageId,
                          refId: widgetRef.id,
                          scenarioId: pageContext.scenarioId,
                          widgetId,
                        },
                      }
                    : undefined,
                ).asObservable,
            ),
            // NOTE: Skip rendering if variables haven't changed by comparing deeply
            // to prevent unnecessary re-renders from identical variable states
            rxjs.distinctUntilChanged(isDeepEqual),
            // Prepare object with required data for rendering before debouncing
            // in the outer observable
            rxjs.map(widgetVariables => ({
              pageContext,
              widgetRef,
              widgetVariables,
            })),
          ),
      ),
      // NOTE Debounce the rendering process to prevent excessive rendering
      // requests when variables are changed rapidly
      rxjs.debounceTime(DEBOUNCE_TIME),
      rxjs.map(({ pageContext, widgetVariables, widgetRef }) =>
        widgetsFeature.actions.render
          .from('triggerModalWidgetRendering (effect)')
          .create({
            pageContext,
            ref: widgetRef,
            variables: {
              // REVIEW: Is this really needed what is use case?
              ...widgetRef.variables,
              ...widgetVariables,
            },
          }),
      ),
    ),
)
